import React, { useContext, useRef } from "react";
import _ from 'lodash';
import cx from 'classnames';

import { AssetCard, PlaceholderAssetCard } from "../assetCard"

import { AssetLayoutProps, AssetsLayoutEnum, StaticAssetsLayoutProps } from "../../types";
import Button from "src/components/button";

import styles from './layout.module.scss';
import { ScrollableContainer } from "src/components/scroller";
import { arrayMove, rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { useDndMonitor } from "@dnd-kit/core";
import { nanoid } from "nanoid";
import useMeasure from "react-use-measure";
import { DealRoomContext } from "../context";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { rearrageDealRoomAssets } from "src/redux/actions/dealRoom";
import InfiniteScroll from "src/components/infiniteScroll";

const DraggableAssetsLayout: React.FC<AssetLayoutProps> = ({ assetsClassName, isRenderingSection = false, loading, activeAssetLayout, assets, onClick, onRemove, onMove, onMoveToSection, fetchAssetsForPage }) => {
  const idRef = useRef(nanoid(6));
  const [carouselRef, carouselBounds] = useMeasure();
  const { dealRoom, menuState } = useContext(DealRoomContext);

  const editing = menuState.primaryAction === 'edit';
  const items = _.get(assets, "items", []);
  const showPlaceholder = loading && _.size(items) === 0;
  const empty = !loading && _.size(items) === 0;
  const dispatch = useDispatch();
  useDndMonitor({
    onDragEnd(event) {
      const { active, over } = event;
      const sourceContainer = _.get(active, 'data.current.sortable.containerId');
      const asset = _.get(active, 'data.current.asset');
      const section = _.get(over, 'data.current.section');
      const target = _.get(over, 'data.current.asset');

      const oldIndex = _.findIndex(items, (item) => _.get(item, 'id') === active.id);
      const newIndex = _.findIndex(items, (item) => _.get(item, 'id') === over?.id);
      const updatedArray = arrayMove(items, oldIndex, newIndex);

      if (idRef.current === sourceContainer && asset) {
        if (section) {
          onMoveToSection(section.index, asset);
        } else if (target) {
          onMove(updatedArray);
        }
      }
    }
  });

  const handleAssetRemove = (asset) => {
    if (onRemove) {
      onRemove(asset);
    }
  }
  
  const handleLoadMore = () => {
    if(editing) return;
    const nextPage = assets.pagination.currentPage + 1;
    fetchAssetsForPage(nextPage);
  }

  return (
    <div className={styles.carousel} ref={carouselRef}>

      {empty && <EmptyAssetsMessage canAdd={true} />}

      <div>
          <SortableContext id={idRef.current} items={items.map(asset => asset.id || '')} strategy={rectSortingStrategy}>
            <div>
              {!empty && activeAssetLayout === AssetsLayoutEnum.LIST && <ListAssetHeader />}
              <InfiniteScroll className={cx(styles.assets, {
                [styles.listView]: activeAssetLayout === AssetsLayoutEnum.LIST,
                [styles.sectionView]: isRenderingSection
              })}
                loading={_.get(assets, "loading", false)}
                hasMore={!editing && _.get(assets, "pagination.hasMoreItems", false)}
                loadMore={handleLoadMore}
                loader={() => {
                  return (
                    <>
                      {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                        <>
                          {activeAssetLayout === AssetsLayoutEnum.LIST && <ListPlaceholderCards key={index} />}
                          {activeAssetLayout === AssetsLayoutEnum.GRID && <PlaceholderAssetCard key={index} />}
                        </>
                      ))}
                    </>
                  )
                }}>
                {items.map((asset) => {
                  return (
                    <AssetCard
                      key={asset.id}
                      asset={asset}
                      editable={editing}
                      onClick={onClick}
                      onRemove={handleAssetRemove}
                      activeAssetLayout={activeAssetLayout}
                    />
                  )
                })}
              </InfiniteScroll>
            </div>

          </SortableContext>
      </div>
    </div >
  );
}

const EmptyAssetsMessage: React.FC<{ canAdd: boolean }> = ({ canAdd }) => {

  const { onMenuChange } = useContext(DealRoomContext);

  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'add-asset',
    });
  }

  return (
    <div className={styles.empty}>
      <div className={cx(styles.card, styles.image)}>
        <img src="/images/dealRoom/empty_internal_assets.svg" />
      </div>
      <div className={cx(styles.card, styles.message)}>
        <p> No assets added yet.</p>
        {canAdd && <Button variant={'tertiary'} className={styles.addAsset} icon="paperflite-add" text="Add asset" onClick={handleAddAsset} />}

      </div>
      <div className={cx(styles.card)}></div>
    </div>
  );
}

const ListPlaceholderCards: React.FC<{ key: number }> = ({ key }) => {
  return (
    <div key={key} className={cx(styles.placeholder)}>
      <div className={cx(styles.thumbnail, styles.block)}></div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <p className={cx(styles.count, styles.block)}></p>
      </div>
    </div>
  )
}


const ListAssetHeader = () => {
  return (
    <Row className={styles.header}>
      <Col xs={4}>
      </Col>
      <Col xs={3}>
        Detail
      </Col>
      <Col xs={2}>
        Uploaded by
      </Col>
      <Col xs={2}>
        Uploaded date
      </Col>
      <Col xs={1}>
        Time spent
      </Col>

    </Row>
  )
}

const StaticAssetsLayout: React.FC<StaticAssetsLayoutProps> = ({ loading, canAddAsset, activeAssetLayout, assets, onClick, fetchAssetsForPage }) => {
  const idRef = useRef(nanoid(6));
  const [carouselRef, carouselBounds] = useMeasure();
  const { menuState } = useContext(DealRoomContext);

  const editing = menuState.primaryAction === 'edit';
  const items = _.get(assets, "items", []);
  const empty = !loading && _.size(items) === 0;

  const handleLoadMore = () => {
    if(editing) return;
    const nextPage = assets.pagination.currentPage + 1;
    fetchAssetsForPage(nextPage);
  }

  return (
    <div className={styles.carousel} ref={carouselRef}>

      {empty && <EmptyAssetsMessage canAdd={canAddAsset} />}

      <div>
        <div> 

          {!empty && activeAssetLayout === AssetsLayoutEnum.LIST && <ListAssetHeader />}
          <InfiniteScroll className={cx(styles.assets, {
            [styles.listView]: activeAssetLayout === AssetsLayoutEnum.LIST
          })}
            loading={_.get(assets, "loading", false)}
            hasMore={!editing && _.get(assets, "pagination.hasMoreItems", false)}
            loadMore={handleLoadMore}
            loader={() => {
              return (
                <>
                  {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                    <>
                      {activeAssetLayout === AssetsLayoutEnum.LIST && <ListPlaceholderCards key={index} />}
                      {activeAssetLayout === AssetsLayoutEnum.GRID && <PlaceholderAssetCard key={index} />}
                    </>
                  ))}
                </>
              )
            }}>
            {items?.map((asset) => {
              return (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  editable={false}
                  onClick={onClick}
                  onRemove={() => ""}
                  activeAssetLayout={activeAssetLayout}
                />
              )
            })}
          </InfiniteScroll>

        </div>
      </div>
    </div >
  );
}

export { DraggableAssetsLayout, StaticAssetsLayout };